import { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import { feelingById, feelingByScore, type FeelingId } from '../constants/bridge';

export interface HomeSignal {
  feeling: FeelingId | null;
  feelingAt: string | null;
  total: number;
  done: number;
  /** Reflections in the last week the therapist has not answered with a heart yet. */
  waiting: number;
}

/** One batched read of what came back from each patient's home this week. */
export default function useHomeSignals(patientIds: string[]) {
  const [signals, setSignals] = useState<Record<string, HomeSignal>>({});
  const key = patientIds.join(',');

  useEffect(() => {
    if (patientIds.length === 0) return;
    let cancelled = false;
    const since = new Date(Date.now() - 7 * 864e5).toISOString();
    (async () => {
      const [moods, missions, refl] = await Promise.all([
        supabase.from('mood_logs').select('user_id, mood_score, mood_type, created_at')
          .in('user_id', patientIds).gte('created_at', since).order('created_at', { ascending: false }),
        supabase.from('missions').select('user_id, is_completed').in('user_id', patientIds),
        supabase.from('mission_reflections').select('user_id, feeling, created_at, therapist_heart_at')
          .in('user_id', patientIds).gte('created_at', since).order('created_at', { ascending: false }),
      ]);
      if (cancelled) return;
      const out: Record<string, HomeSignal> = {};
      for (const id of patientIds) out[id] = { feeling: null, feelingAt: null, total: 0, done: 0, waiting: 0 };

      type Mood = { user_id: string; mood_score: number; mood_type?: string; created_at: string };
      type Refl = { user_id: string; feeling: string; created_at: string; therapist_heart_at: string | null };
      const latest = (s: HomeSignal, f: FeelingId | undefined, at: string) => {
        if (f && (!s.feelingAt || at > s.feelingAt)) { s.feeling = f; s.feelingAt = at; }
      };
      for (const m of (moods.data ?? []) as Mood[]) {
        const s = out[m.user_id];
        if (s) latest(s, (feelingById(m.mood_type) ?? feelingByScore(m.mood_score))?.id, m.created_at);
      }
      if (!refl.error) {
        for (const r of (refl.data ?? []) as Refl[]) {
          const s = out[r.user_id];
          if (!s) continue;
          latest(s, feelingById(r.feeling)?.id, r.created_at);
          if (!r.therapist_heart_at) s.waiting += 1;
        }
      }
      for (const m of (missions.data ?? []) as { user_id: string; is_completed: boolean }[]) {
        const s = out[m.user_id];
        if (!s) continue;
        s.total += 1;
        if (m.is_completed) s.done += 1;
      }
      setSignals(out);
    })().catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return signals;
}
